import * as Sentry from '@sentry/vue';
import { computed, onScopeDispose, ref, watch } from 'vue';
import { useQuery, useQueryClient } from '@tanstack/vue-query';
import { toast } from 'vue-sonner';
import { storeToRefs } from 'pinia';
import { useAsyncOperationSubscription } from '@/composables/useAsyncOperationSubscription';
import {
  fetchActiveBulkRun,
  fetchBulkRun,
  fetchBulkRunRecordIds,
  isBulkRunActive,
  isBulkRunFinished,
  readBulkApiError,
  type BulkRun,
} from '@/composables/api/useBulkActionApi';
import {
  SCHEDULE_ENTITY_SLUG,
  applyBulkRunToCaches,
  snapshotSchedulesBeforeRun,
  type SchedulesBeforeRun,
} from '@/composables/api/bulkActionCachePatch';
import { useBulkActionRunStore } from '@/stores/bulkActionRunStore';

/** The polling fallback's cadence while a run is queued or running, from the API contract. */
export const BULK_RUN_POLL_MS = 3000;

/** What the user is told once the rows are patched. The search index lags the database. */
export const BULK_RUN_CATCH_UP_NOTE = 'The search catches up within seconds.';

export function bulkRunQueryKey(runId: number | null) {
  return ['bulk-action-run', runId] as const;
}

/** "142 schedules changed" and similar, for the finished toast and the bar. */
export function bulkRunDoneSummary(run: BulkRun): string {
  const done = run.outcomes?.done ?? 0;
  const noun = run.entity === 'location_zone_custom_playlist' ? 'list' : 'schedule';
  const nouns = `${done.toLocaleString()} ${noun}${done === 1 ? '' : 's'}`;
  switch (run.action) {
    case 'delete':
      return `${nouns} deleted.`;
    case 'add_tag':
      return `Tags added to ${nouns}.`;
    case 'remove_tag':
      return `Tags removed from ${nouns}.`;
    default:
      return `${nouns} changed.`;
  }
}

/** How many items did not end as `done` or `unchanged`. */
export function bulkRunFailedCount(run: BulkRun): number {
  if (!run.outcomes) return 0;
  return Object.entries(run.outcomes)
    .filter(([outcome]) => outcome !== 'done' && outcome !== 'unchanged')
    .reduce((sum, [, count]) => sum + (count ?? 0), 0);
}

/**
 * The cached schedules as they read just before this browser executed each schedule run, keyed by
 * run id. It is kept in memory only. After a reload it is gone, and a shift falls back to a refetch.
 * An entry is removed once the run's rows are patched, when the run is dismissed or replaced, and
 * when the bar that started the run unmounts. So it never outlives the page that needs it.
 */
const schedulesBeforeRunByRunId = new Map<number, SchedulesBeforeRun>();

/**
 * Follow the user's bulk-action run from "queued" to its report.
 *
 * Two paths tell the app the run has finished, and either may win.
 * - The async-operation signal on `private-async-operations_<operation_id>`. It carries no counts,
 *   so on it the run is fetched again.
 * - A poll of `GET /api/bulk-action-runs/{run}` every three seconds while the run is queued or
 *   running. It covers a missed broadcast and a backgrounded tab.
 *
 * The run id lives in `bulkActionRunStore`, which is persisted, so a reload picks the run up
 * again. With nothing stored, the composable asks `GET /api/bulk-action-runs/active` once.
 *
 * When a run finishes, the cached search rows are patched once, and the user is told the search
 * catches up within seconds. The rows are only patched when this page saw the run working. A
 * shift is patched only for a schedule run this page started, from the schedules it had cached
 * when it started. Otherwise a page that fetched mid-run may already hold the shifted value, so
 * the searches are fetched again instead (see `applyBulkRunToCaches`).
 */
export function useBulkActionRun() {
  const queryClient = useQueryClient();
  const store = useBulkActionRunStore();
  const { trackedRun } = storeToRefs(store);

  store.pruneExpired();

  const runId = computed(() => trackedRun.value?.runId ?? null);

  // The run ids this bar recorded a snapshot for. They are dropped when the bar unmounts.
  const snapshotRunIds = new Set<number>();
  function dropSnapshot(droppedRunId: number): void {
    schedulesBeforeRunByRunId.delete(droppedRunId);
    snapshotRunIds.delete(droppedRunId);
  }
  // A run that is dismissed, or replaced by the next one, no longer needs its snapshot.
  watch(runId, (nextRunId, previousRunId) => {
    if (previousRunId !== null && previousRunId !== nextRunId) dropSnapshot(previousRunId);
  });
  onScopeDispose(() => {
    for (const snapshotRunId of [...snapshotRunIds]) dropSnapshot(snapshotRunId);
  });

  const runQuery = useQuery({
    queryKey: computed(() => bulkRunQueryKey(runId.value)),
    queryFn: () => fetchBulkRun(runId.value as number),
    enabled: computed(() => runId.value !== null),
    refetchInterval: (query) => (isBulkRunActive(query.state.data?.status) ? BULK_RUN_POLL_MS : false),
    refetchIntervalInBackground: true,
    retry: 0,
  });

  const run = computed<BulkRun | null>(() => (runId.value === null ? null : (runQuery.data.value ?? null)));

  // In an action on lists and schedules, the schedule run finished before the list run started.
  // It is final, so it is fetched once and never polled.
  const previousRunId = computed(() => trackedRun.value?.previousRun?.runId ?? null);
  const previousRunQuery = useQuery({
    queryKey: computed(() => bulkRunQueryKey(previousRunId.value)),
    queryFn: () => fetchBulkRun(previousRunId.value as number),
    enabled: computed(() => previousRunId.value !== null),
    retry: 0,
  });
  const previousRun = computed<BulkRun | null>(() =>
    previousRunId.value === null ? null : (previousRunQuery.data.value ?? null),
  );
  const isActive = computed(() => isBulkRunActive(run.value?.status));
  const isFinished = computed(() => isBulkRunFinished(run.value?.status));

  const progressPercent = computed(() => {
    const progress = run.value?.progress;
    if (!progress || progress.total <= 0) return 0;
    return Math.min(100, Math.round((progress.processed / progress.total) * 100));
  });

  // A run the server has pruned, or one that is not this user's, can never resolve.
  watch(
    () => runQuery.error.value,
    (error) => {
      if (!error) return;
      const { status } = readBulkApiError(error);
      if (status === 404 || status === 403) store.forget();
    },
  );

  // The channel is joined only while the run is queued or running. A finished run's operation
  // is gone from the server 30 seconds after it finishes, so a page that still shows its report
  // and joined again would be refused on every load. The poll covers the moment before the first
  // fetch tells us the run is working.
  const operationId = computed(() => {
    if (!isActive.value) return null;
    return trackedRun.value?.operationId ?? run.value?.async_operation?.operation_id ?? null;
  });

  useAsyncOperationSubscription(operationId, {
    onFinished: () => {
      void runQuery.refetch();
    },
  });

  /** True once this page has seen the run queued or running. */
  const sawRunWorking = ref(false);
  const handledRunIds = new Set<number>();

  async function handleFinished(finishedRun: BulkRun): Promise<void> {
    if (handledRunIds.has(finishedRun.id)) return;
    handledRunIds.add(finishedRun.id);

    const entry = trackedRun.value;
    if (entry?.runId === finishedRun.id && !entry.cachePatched && sawRunWorking.value) {
      try {
        const doneRecordIds = await fetchBulkRunRecordIds(finishedRun.id, ['done']);
        applyBulkRunToCaches(queryClient, {
          entity: finishedRun.entity,
          operation: finishedRun.operation,
          doneRecordIds,
          valueLabels: entry.valueLabels,
          schedulesBeforeRun: schedulesBeforeRunByRunId.get(finishedRun.id),
        });
      } catch (error) {
        // The report is still correct. Only the cached rows stay old until the next search.
        void queryClient.invalidateQueries({ queryKey: ['search', finishedRun.entity], refetchType: 'none' });
        Sentry.captureException(error);
      }
    }
    store.markCachePatched(finishedRun.id);
    dropSnapshot(finishedRun.id);

    if (!sawRunWorking.value) return;
    if (finishedRun.status === 'failed') {
      toast.error('The bulk action stopped before it finished', {
        description: `${bulkRunDoneSummary(finishedRun)} Open the report to see which rows were not reached.`,
      });
      return;
    }
    const failed = bulkRunFailedCount(finishedRun);
    toast.success(bulkRunDoneSummary(finishedRun), {
      description: failed > 0
        ? `${failed.toLocaleString()} could not be changed. ${BULK_RUN_CATCH_UP_NOTE}`
        : BULK_RUN_CATCH_UP_NOTE,
    });
  }

  watch(
    run,
    (current) => {
      if (!current) return;
      if (current.async_operation && trackedRun.value?.operationId == null) {
        store.attachOperation(current.id, current.async_operation.operation_id, current.async_operation.scope);
      }
      if (isBulkRunActive(current.status)) {
        sawRunWorking.value = true;
        return;
      }
      if (isBulkRunFinished(current.status)) {
        void handleFinished(current);
      }
    },
    { immediate: true },
  );

  // Nothing stored: a run may still be going, started before a reload or in another tab.
  if (trackedRun.value === null) {
    fetchActiveBulkRun()
      .then((activeRun) => {
        if (!activeRun || trackedRun.value !== null) return;
        store.track({
          runId: activeRun.id,
          operationId: activeRun.async_operation?.operation_id ?? null,
          scope: activeRun.async_operation?.scope ?? null,
          entity: activeRun.entity,
          startedAt: Date.now(),
        });
        queryClient.setQueryData(bulkRunQueryKey(activeRun.id), activeRun);
      })
      .catch((error) => {
        // Resuming is a convenience. The run carries on, and its report is reachable later.
        Sentry.captureException(error);
      });
  }

  /**
   * The cached schedules as they read now, for a schedule run. Call it just before the execute
   * request and pass the result to {@link start}. A live poll that lands while the request is in
   * flight may already hold shifted dates, so a snapshot taken after it could shift them twice.
   * Undefined for a list run, which has no shift to patch.
   */
  function snapshotBeforeExecute(entitySlug: string): SchedulesBeforeRun | undefined {
    return entitySlug === SCHEDULE_ENTITY_SLUG ? snapshotSchedulesBeforeRun(queryClient) : undefined;
  }

  /**
   * Start following a run that execute has just accepted. `previousRun` is the run that finished
   * just before it in the same action, so the report can show both. `schedulesBeforeRun` is what
   * {@link snapshotBeforeExecute} returned before the request.
   */
  function start(
    executedRun: BulkRun,
    valueLabels?: Record<string, string>,
    previousRun?: BulkRun | null,
    schedulesBeforeRun?: SchedulesBeforeRun,
  ): void {
    // This page started the run, so its search results were fetched before the change. Patching
    // them once is right even when the run has already finished, as it does when the queue runs
    // jobs inside the request.
    sawRunWorking.value = true;
    if (schedulesBeforeRun !== undefined) {
      schedulesBeforeRunByRunId.set(executedRun.id, schedulesBeforeRun);
      snapshotRunIds.add(executedRun.id);
    }
    store.track({
      runId: executedRun.id,
      operationId: executedRun.async_operation?.operation_id ?? null,
      scope: executedRun.async_operation?.scope ?? null,
      entity: executedRun.entity,
      startedAt: Date.now(),
      valueLabels,
      previousRun: previousRun ? { runId: previousRun.id, entity: previousRun.entity } : null,
    });
    queryClient.setQueryData(bulkRunQueryKey(executedRun.id), executedRun);
    if (previousRun) queryClient.setQueryData(bulkRunQueryKey(previousRun.id), previousRun);
  }

  /** Close the report. The run stays on the server for 30 days. */
  function dismiss(): void {
    store.forget();
  }

  return { run, runId, previousRun, isActive, isFinished, progressPercent, start, snapshotBeforeExecute, dismiss };
}
